import Service, { type Registry as Services, service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import type {
  Featurescontext,
  Hotel,
  Market,
  Reportgroup,
  User,
} from 'frontend-utils/models';
import { BENI_FEATURE_FLAGS } from 'lumen/utils/constants/benchmark-insight/packages/beni-feature-flag-constants';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { UNIFIED_SETTINGS_FEATURES } from 'lumen/utils/constants/unified-settings-feature-constants';
import { type Permission, permissionChecks } from 'lumen/utils/permissions';

export default class AccessService extends Service {
  @service declare destination: Services['destination'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare navigation: Services['navigation'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  get activeFeaturesContext(): Featurescontext | undefined {
    const { destinationSubscription, hotel, market, user } = this;

    if (!user) {
      return undefined;
    }

    if (hotel) {
      const targetId = hotel.id;

      return user.featurescontext.find(({ subscription }) => {
        return subscription === targetId;
      });
    }

    if (market) {
      const targetId = market.id;

      return user.featurescontext.find(({ market }) => {
        return market?.id === targetId;
      });
    }

    if (destinationSubscription) {
      const targetId = destinationSubscription.id;

      return user.featurescontext.find(({ destinationSubscription }) => {
        return destinationSubscription?.id === targetId;
      });
    }

    const targetId = user.id;

    return user.featurescontext.find(({ user }) => {
      return user?.id === targetId;
    });
  }

  // We've added specific feature flags for different inputs in the details block because of resellers (e.g.: Tripadvisor clients should only be able to change their language)
  get arePersonalSettingsDetailsEditable(): boolean {
    return (
      this.hasFeature('user.personal_settings.edit.detail.name') ||
      this.hasFeature('user.personal_settings.view.detail.email') ||
      this.hasFeature('user.personal_settings.edit.detail.language')
    );
  }

  get canEditAppCompset(): boolean {
    return this.hasFeature('settings.compset.app.edit');
  }

  get canEditPersonalSettingsPassword(): boolean {
    return this.hasFeature('user.personal_settings.edit.password');
  }

  get canEditRmsCompset(): boolean {
    return this.hasFeature('settings.compset.rms.edit');
  }

  get deniedFeatures(): string[] {
    const featureFlags = Array.from(this.featureFlags);

    // Return all denied FFs but explicitly filter out the denied context FF
    return featureFlags.filter((featureFlag) => {
      return (
        featureFlag.startsWith('denied.') &&
        featureFlag !== 'denied.user.staff.context'
      );
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get destinationSubscription() {
    return this.destination.destinationSubscription;
  }

  @cached get featureFlags(): Set<string> {
    const hasUnifiedHotelView =
      this.featureToggle.featureFlagsAvailable &&
      this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.UNIFIED_HOTEL_VIEW);

    if (hasUnifiedHotelView && this.hotel?.id) {
      // Use features for the current subscription
      const features = this.navigation.getFeatures(this.hotel.id);

      if (features.size > 0) {
        return features;
      }
    }

    const features = this.activeFeaturesContext?.availableFeatures ?? [];

    return new Set(features.toSorted());
  }

  get hasAccessCompsets(): boolean {
    return (
      this.hasCompsetBenchmark ||
      this.hasAppCompset ||
      this.hasRmsCompset ||
      this.hasCompsetBenchmarkSmartCompset
    );
  }

  get hasAppCompset(): boolean {
    return this.hasFeature('settings.compset.app.view');
  }

  get hasBenchmarkInsight(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.APP);
  }

  get hasBenchmarkInsightAdminRole(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.ROLE_ADMIN);
  }

  get hasBenchmarkInsightChannel(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.CHANNEL);
  }

  get hasBenchmarkInsightCompset(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.COMPSET);
  }

  get hasBenchmarkInsightData(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.DATA);
  }

  get hasBenchmarkInsightDataStaffOnly(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.DATA_STAFF_ONLY);
  }

  get hasBenchmarkInsightExport(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.EXPORTS);
  }

  get hasBenchmarkInsightFutureData(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.FUTURE_DATA);
  }

  get hasBenchmarkInsightLengthOfStay(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.LENGTH_OF_STAY);
  }

  get hasBenchmarkInsightLightUpsell(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.LIGHT_UPSELL);
  }

  get hasBenchmarkInsightLightUpsellEdit(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.LIGHT_UPSELL_EDIT);
  }

  get hasBenchmarkInsightMappingEditRights(): boolean {
    return (
      this.hasBenchmarkInsightMappingTab &&
      this.hasFeature(BENI_FEATURE_FLAGS.MAPPING_EDIT)
    );
  }

  get hasBenchmarkInsightMappingTab(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.MAPPING_VIEW);
  }

  get hasBenchmarkInsightPace(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.PACE);
  }

  get hasBenchmarkInsightPickupData(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.PICKUP);
  }

  get hasBenchmarkInsightRanking(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.RANKING);
  }

  get hasBenchmarkInsightReferencePeriodData(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.REFERENCE_PERIOD);
  }

  get hasBenchmarkInsightSegmentationPartial(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.SEGMENTATION_PARTIAL);
  }

  get hasBenchmarkInsightSettingsCompsetEdit(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.SETTINGS_COMPSET_EDIT);
  }

  get hasBenchmarkInsightSmartCompset(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.SMART_COMPSET);
  }

  get hasBenchmarkInsightSmartCompsetEdit(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.SETTINGS_SMART_COMPSET_EDIT);
  }

  get hasBenchmarkInsightTermsAndConditionsAccept(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.TERMS_AND_CONDITIONS_ACCEPT);
  }

  get hasBenchmarkInsightTrialUpsell(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.TRIAL_UPSELL);
  }

  get hasBenchmarkInsightTrialUpsellEdit(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.TRIAL_UPSELL_EDIT);
  }

  get hasBenchmarkInsightWriterRole(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.ROLE_WRITER);
  }

  get hasBudgetAndForecast(): boolean {
    return this.hasFeature('app.business_intelligence.budget_and_forecast');
  }

  get hasBudgetAndForecastFinanceExport(): boolean {
    return this.hasFeature(
      'business_intelligence.budget_and_forecast.finance_export',
    );
  }

  get hasBudgetAndForecastSettings(): boolean {
    return this.hasFeature(
      'business_intelligence.settings.budget_and_forecast',
    );
  }

  get hasCentralMapping(): boolean {
    return this.hasFeature('settings.mapping');
  }

  get hasCentralMappingCategoriesEditAccess(): boolean {
    return this.hasFeature('settings.mapping.categories.edit');
  }

  get hasCentralMappingCategoriesMarketEditAccess(): boolean {
    return this.hasFeature('settings.mapping.categories.market.edit');
  }

  get hasCentralMappingCategoriesMarketViewAccess(): boolean {
    return this.hasFeature('settings.mapping.categories.market.view');
  }

  get hasCentralMappingEditAccess(): boolean {
    return this.hasFeature('settings.mapping.pms.edit');
  }

  get hasCentralMappingMarketEditAccess(): boolean {
    return this.hasFeature('settings.mapping.pms.market.edit');
  }

  get hasCentralMappingMarketViewAccess(): boolean {
    return this.hasFeature('settings.mapping.pms.market.view');
  }

  get hasCentralMappingViewAccess(): boolean {
    return this.hasFeature('settings.mapping.pms.view');
  }

  get hasChannelManagerTab(): boolean {
    return this.hasFeature('app.channel_manager');
  }

  get hasCompsetBenchmark(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.SETTINGS_COMPSET_VIEW);
  }

  get hasCompsetBenchmarkSmartCompset(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.SETTINGS_SMART_COMPSET_VIEW);
  }

  get hasCurrencyConversionEditAccess(): boolean {
    return this.hasFeature('settings.exchange_rate_profiles.edit');
  }

  get hasCurrencyConversionViewAccess(): boolean {
    return this.hasFeature('settings.exchange_rate_profiles.view');
  }

  get hasCustomReporting(): boolean {
    return this.hasFeature('app.business_intelligence.custom_reporting');
  }

  get hasCustomReportingEdit(): boolean {
    // if we're on a destination page, disallow editing
    if (this.destination.destinationSubscription) {
      return false;
    }

    return this.hasFeature('business_intelligence.custom_reporting.edit');
  }

  get hasDaaSCustomReporting(): boolean {
    return this.hasFeature('app.daas.custom_reporting');
  }

  get hasDataReplatforming(): boolean {
    return this.hasFeature('spider.data_replatforming');
  }

  get hasDeniedFeatures(): boolean {
    return this.deniedFeatures.length > 0;
  }

  get hasDeniedFeaturesOnly(): boolean {
    return this.hasDeniedFeatures && !this.hasRestrictedFeatures;
  }

  get hasEvents(): boolean {
    return this.hotel ? this.can('app.events') : this.can('app.market.events');
  }

  /*
    Check for FF if we can allow hotel level restrictions
    https://docs.google.com/presentation/d/1KPuwYRwYPgN2KeHMTqarkA5qO1M1yYZ7mJRAh1khBN0/edit?slide=id.p#slide=id.p
  */
  get hasHotelRestrictionSupport(): boolean {
    return this.hasFeature('pricing_assistant.pms.hotel_restriction_push');
  }

  get hasLimitedParityView(): boolean {
    return this.hasFeature('app.limited_parity');
  }

  get hasMarketInsight(): boolean {
    return this.hasFeature('app.market_insight');
  }

  get hasMarketOtb(): boolean {
    return this.hasFeature('otb.market_benchmark');
  }

  get hasOTB(): boolean {
    return this.hasFeature('pms.otb.view');
  }

  get hasOTBForecastView(): boolean {
    return this.hasFeature('pms.otb_forecast.view');
  }

  get hasOTBView(): boolean {
    return (
      this.hasFeature('otb.empty_placeholder') ||
      this.hasFeature('otb.data_validation') ||
      this.hasFeature('pms.otb.view')
    );
  }

  get hasOverviewDashboardEditRights(): boolean {
    return this.hasFeature('overview.edit');
  }

  get hasOverviewDashboardTab(): boolean {
    return this.hasFeature('overview.view');
  }

  get hasParity(): boolean {
    return this.hotel
      ? this.can('app.parity', this.hotelOrMarket)
      : this.can('app.market.parity', this.hotelOrMarket);
  }

  get hasParityAnalytics(): boolean {
    return this.hasFeature('app.hotel.parity_analytics');
  }

  get hasParityAnalyticsStaff(): boolean {
    if (!this.hotel) {
      return false;
    }

    // Checks if the current hotel can also view PA data in parity tab. Feature flag is only set for Staff users!
    return (
      this.hasFeature('app.hotel.parity_analytics.staff') &&
      this.hotel.OTAsParityAnalytics.length > 0
    );
  }

  get hasParityCustomLiveupdate(): boolean {
    return this.hasFeature('parity.liveshop.custom');
  }

  get hasParityDayLiveupdate(): boolean {
    return this.hasFeature('parity.liveshop.day');
  }

  get hasParityInsightAccess(): boolean {
    return this.hasFeature('app.market.parity');
  }

  get hasParityManagerAccess(): boolean {
    return this.hasFeature('app.market.parity_manager');
  }

  // Flag used in parity tab to check if we can show the overview dashboard
  get hasParityOverview(): boolean {
    // Hotel could be null in some cases, see https://otainsight.atlassian.net/browse/SH-10083
    if (!this.hotel) {
      return false;
    }

    const ownHotelHasBranddotcom =
      this.hotel.hotelinfo.otas.filter((ota) => ota.id === 'branddotcom')
        .length > 0;
    const branddotcomInOTAsParity =
      this.hotel.OTAsParity.filter((ota) => ota.id === 'branddotcom').length >
      0;

    return (
      this.hasParity &&
      ownHotelHasBranddotcom &&
      branddotcomInOTAsParity &&
      !this.hasLimitedParityView
    );
  }

  get hasParityStrategyAccess(): boolean {
    return this.hasFeature('app.market.parity_strategy');
  }

  get hasPerformanceDashboard(): boolean {
    return this.hasFeature('app.performance_dashboard');
  }

  get hasPersonalSettingsMFA(): boolean {
    return this.hasFeature('user.personal_settings.view.mfa');
  }

  get hasPersonalSettingsTab(): boolean {
    return (
      this.arePersonalSettingsDetailsEditable ||
      this.hasFeature('user.personal_settings.edit.password') ||
      this.hasFeature('user.personal_settings.edit.other')
    );
  }

  /**
   * This method is to fulfil the new Indies Plan navigation.
   * For more: https://lighthouseintelligence.atlassian.net/browse/INDU-863
   * */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasPricingAndDistributionTab() {
    return (
      this.hasFeature('app.channel_manager') && this.hasPricingAssistantTab
    );
  }

  get hasPricingAssistantDynamicRatePricingFeature(): boolean {
    return this.hasFeature('pricing_assistant.pms.dynamic_rate_pricing');
  }

  get hasPricingAssistantEditRights(): boolean {
    return this.hasFeature('pricing_assistant.recommendations.edit');
  }

  get hasPricingAssistantMinLosRestrictionsFeature(): boolean {
    return this.hasFeature('pricing_assistant.min_los_restrictions');
  }

  get hasPricingAssistantOpenPricingFeature(): boolean {
    return this.hasFeature('pricing_assistant.pms.open_room_pricing');
  }

  get hasPricingAssistantOverbookingRestrictionsFeature(): boolean {
    return this.hasFeature('pricing_assistant.overbooking_restrictions');
  }

  get hasPricingAssistantRestrictionsEditRights(): boolean {
    return this.hasFeature('pricing_assistant.restrictions.edit');
  }

  get hasPricingAssistantSettingsAutopilotTab(): boolean {
    if (this.isPrimaHidden) {
      return false;
    }

    // Hide autopilot tab for DRP subs to disallow enabling autopilot for read-only subs
    if (
      this.hasPricingAssistantDynamicRatePricingFeature &&
      !this.isStaffUser
    ) {
      return false;
    }

    // Autopilot tab is shown if PMS is integrated AND the integrated PMS supports autopilot
    return (
      this.hasFeature('pricing_assistant.pms.push_integrated') &&
      this.hasFeature('pricing_assistant.pms.autopilot')
    );
  }

  get hasPricingAssistantSettingsEditRights(): boolean {
    return this.hasFeature('pricing_assistant.settings.edit');
  }

  get hasPricingAssistantSettingsPmsIntegrationTab(): boolean {
    if (this.isPrimaHidden) {
      return false;
    }

    // Show tab in case the PMS is integrated or the integration signup process is available or in progress
    return (
      this.hasFeature('pricing_assistant.pms.push_integrated') ||
      this.hasFeature('pricing_assistant.signup.open') ||
      this.hasFeature('pricing_assistant.signup.in_progress')
    );
  }

  get hasPricingAssistantSettingsPreferredRateTab(): boolean {
    // Preferred rate tab is shown if the sub has PA and its PMS is not integrated (for the first version)
    return (
      this.hasPricingAssistantTab &&
      !this.hasFeature('pricing_assistant.pms.push_integrated')
    );
  }

  get hasPricingAssistantTab(): boolean {
    if (this.isPrimaHidden) {
      return false;
    }

    return this.hasFeature('app.pricing_assistant');
  }

  get hasPricingAssistantViewRights(): boolean {
    // We hide push CTAs for pilot subs. This FF should be removed when the pilot is over
    if (this.hasFeature('pricing_assistant.subscription.radisson_pilot')) {
      return false;
    }

    // If this FF isn't present we hide the Pricing Assistant main page recommendation action (push/snooze) & manual price editing buttons
    return this.hasFeature('pricing_assistant.recommendations.view');
  }

  // Internal user FF: used for sales to indicate when they can edit settings (only before sub is integrated)
  get hasPricingSettingsNonIntegratedEditRights(): boolean {
    return this.hasFeature('pricing.settings.non_integrated.edit');
  }

  // Internal user FF: used for CC teams that have temporary access to edit pricing settings
  get hasPricingSettingsTempEditRights(): boolean {
    return this.hasFeature('pricing.settings.temporary.edit');
  }

  get hasPropertyTags(): boolean {
    return this.hasFeature('settings.property_tags');
  }

  get hasRanking(): boolean {
    return this.can('app.ranking', this.hotelOrMarket);
  }

  get hasRates(): boolean {
    return this.hotel
      ? this.can('app.rates', this.hotelOrMarket)
      : this.can('app.market.rates', this.hotelOrMarket);
  }

  get hasRatesCustomLiveupdate(): boolean {
    return this.hasFeature('rates.liveshop.custom');
  }

  get hasRatesDayLiveupdate(): boolean {
    return this.hasFeature('rates.liveshop.day');
  }

  get hasRatesShoppingPaused(): boolean {
    return this.hasFeature('rates.shopping.paused');
  }

  get hasRateStrategyAccess(): boolean {
    return this.hasFeature('app.rate_strategy');
  }

  get hasReports(): boolean {
    return this.hasFeature('reports.run_download');
  }

  get hasReservationManagerTab(): boolean {
    return this.hasFeature('app.reservation_manager');
  }

  get hasRestrictedFeatures(): boolean {
    return this.restrictedFeatures.length > 0;
  }

  get hasRevenueInsight(): boolean {
    return this.hasFeature('app.revenue_insight');
  }

  get hasReviewManagerReplyGenerate(): boolean {
    return this.hasFeature('review_manager.reply.generate');
  }

  get hasReviewManagerReplyGenerateUpsell(): boolean {
    return this.hasFeature('review_manager.reply.generate.upsell');
  }

  get hasReviewManagerReplyGenerateUpsellTrialRequested(): boolean {
    return this.hasFeature(
      'review_manager.reply.generate.upsell.trial_requested',
    );
  }

  get hasReviewManagerReplyPush(): boolean {
    return this.hasFeature('review_manager.reply.push');
  }

  get hasReviewManagerTab(): boolean {
    return this.hasFeature('app.review_manager');
  }

  get hasRmsCompset(): boolean {
    return this.hasFeature('settings.compset.rms.view');
  }

  get hasRoomTypeAvailability(): boolean {
    return this.hasFeature('pms.rooms.otb.view');
  }

  get hasRoomtypeMappingEditRights(): boolean {
    return this.hasFeature('subscription.roomtypemapping.edit');
  }

  get hasSettingsBenchmarkPortfolioCompsetView(): boolean {
    return this.hasFeature(BENI_FEATURE_FLAGS.SETTINGS_PORTFOLIO_COMPSET_VIEW);
  }

  get hasShortTermRentalAccess(): boolean {
    return this.hasFeature('app.short_term_rental');
  }

  get hasShortTermRentalEditRights(): boolean {
    return this.hasFeature('short_term_rental.filters.edit');
  }

  /*
  For Products to Plan development, we hide STR datapoints for new plan subs in other products due to BE API limitations (they will only be able to see STR data in the specific tab)
  This is a temporary measure until BE can fix the STR API scalability issue
  */
  get hasShortTermRentalOnlyStrTab(): boolean {
    return this.hasFeature('short_term_rental.only_str_tab');
  }

  get hasSmartDistributionClosingTheLoopFeature(): boolean {
    return this.hasFeature('smart_distribution.closing_the_loop.view');
  }

  get hasSmartDistributionTab(): boolean {
    if (this.isPrimaHidden) {
      return false;
    }

    return this.hasFeature('app.smart_distribution');
  }

  get hasSmartInsights(): boolean {
    return this.hasFeature('app.smart_insights');
  }

  get hasSmartInsightsUpsell(): boolean {
    return this.hasFeature('smart_insights.365_days.upsell');
  }

  get hasSmartInsightsYearView(): boolean {
    return this.hasFeature('smart_insights.365_days');
  }

  get hasSpider(): boolean {
    return this.hasFeature('app.spider');
  }

  get hasSpiderEdit(): boolean {
    return this.hasFeature('spider.settings.edit');
  }

  get hasSpiderMarket(): boolean {
    return this.hasFeature('app.market.spider');
  }

  get hasSpiderMarketEdit(): boolean {
    return this.hasFeature('spider.settings.portfolio.edit');
  }

  get hasSpiderRateCodeSync(): boolean {
    return this.hasFeature('revenueinsight.spider_rate_code_sync');
  }

  get hasSpiderRevpakLibrary(): boolean {
    return this.hasFeature('spider.revpak_library');
  }

  get hasTotalRevenue(): boolean {
    return this.hasFeature('app.market.total_revenue');
  }

  get hasTransactionMapping(): boolean {
    return this.hasFeature('settings.transaction_mapping.edit');
  }

  get hasTrophyHighlights(): boolean {
    // Related to Indies ROI Trophy development
    // 1 feature flag for both trophy types (price push trophy & weekly milestones)
    return this.hasFeature('app.highlights');
  }

  get hasUnifiedSettingsChannelDistributionEdit(): boolean {
    return this.hasFeature(UNIFIED_SETTINGS_FEATURES.CHANNEL_DISTRIBUTION_EDIT);
  }

  get hasUnifiedSettingsChannelDistributionView(): boolean {
    return this.hasFeature(UNIFIED_SETTINGS_FEATURES.CHANNEL_DISTRIBUTION_VIEW);
  }

  get hasUnifiedSettingsSubscriptionProfileEdit(): boolean {
    return this.hasFeature(UNIFIED_SETTINGS_FEATURES.SUBSCRIPTION_PROFILE_EDIT);
  }

  get hasUnifiedSettingsSubscriptionProfileView(): boolean {
    return this.hasFeature(UNIFIED_SETTINGS_FEATURES.SUBSCRIPTION_PROFILE_VIEW);
  }

  get hasVisibilityBoostViewAccess(): boolean {
    return this.hasFeature('pricing_assistant.visibility_boost.view');
  }

  get hotel(): Hotel | null {
    return this.hotelAndMarketService.hotel;
  }

  get hotelOrMarket(): Hotel | Market {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  get isBlindUser(): boolean {
    return this.hasFeature('user.blind');
  }

  get isContextDeniedForStaffUsers(): boolean {
    // Entire subscription context is inaccessible for staff users (e.g. all FFs are denied)
    return this.hasFeature('denied.user.staff.context');
  }

  get isContextHiddenForStaffUsers(): boolean {
    return (
      this.isContextRestrictedForStaffUsers || this.isContextDeniedForStaffUsers
    );
  }

  get isContextRestrictedForStaffUsers(): boolean {
    // Entire subscription context is inaccessible for staff users but access can be requested (e.g. due to region)
    return this.hasFeature('restricted.user.staff.context');
  }

  get isMarketInsightFull(): boolean {
    return this.hasFeature('app.market_insight.full');
  }

  get isMarketInsightMarketUnsupported(): boolean {
    return this.hasFeature('app.market_insight.no_data');
  }

  // Note: based on "pricing_assistant.recommendations.edit" FF - use this for PMS pushing related actions & inputs
  get isPricingAssistantActionOrInputDisabled(): boolean {
    return !this.hasPricingAssistantEditRights;
  }

  get isPricingAssistantPmsSettingsActionOrInputDisabled(): boolean {
    return !(
      this.hasPricingAssistantSettingsEditRights ||
      this.hasPricingSettingsTempEditRights
    );
  }

  get isPricingAssistantPreferredRateSettingsActionOrInputDisabled(): boolean {
    return !(
      this.hasPricingAssistantSettingsEditRights ||
      this.hasPricingSettingsTempEditRights ||
      this.hasPricingSettingsNonIntegratedEditRights
    );
  }

  get isPricingAssistantRecommendationSettingsActionOrInputDisabled(): boolean {
    return !(
      this.hasPricingAssistantSettingsEditRights ||
      this.hasPricingSettingsTempEditRights ||
      this.hasPricingSettingsNonIntegratedEditRights
    );
  }

  // Note: based on "pricing_assistant.settings.edit" FF - use this for Pricing Assistant settings related actions/inputs/saving/...
  // Extra note: we have a similar getter for more specific edit cases. This is mainly to work around some cases with temp edit rights for internal users
  get isPricingAssistantSettingsActionOrInputDisabled(): boolean {
    return !this.hasPricingAssistantSettingsEditRights;
  }

  get isPricingAssistantStandaloneVersion(): boolean {
    return this.hasPricingAssistantTab && !this.hasRates;
  }

  // Temporary FF: hides PRIMA for subscriptions that have both PRIMA and Smart Insights
  // until we implement recommendations that work for both products.
  private get isPrimaHidden(): boolean {
    return this.hasFeature('smart_insights.recommendations_hide_prima');
  }

  get isSsoUser(): boolean {
    return this.hasFeature('user.sso');
  }

  get isStaffUser(): boolean {
    return this.hasFeature('user.staff');
  }

  get market(): Market | null {
    return this.hotelAndMarketService.market;
  }

  get restrictedFeatures(): string[] {
    const featureFlags = Array.from(this.featureFlags);

    // Return all restricted FFs but explicitly filter out the restricted context FF
    return featureFlags.filter((featureFlag) => {
      return (
        featureFlag.startsWith('restricted.') &&
        featureFlag !== 'restricted.user.staff.context'
      );
    });
  }

  get user(): User {
    return this.hotelAndMarketService.user;
  }

  can(
    permission: Permission,
    resource?: Hotel | Market | Reportgroup,
  ): boolean {
    const config = permissionChecks[permission];

    if (!config) {
      return false;
    }

    const meetsFeatures = config.flags.every((flag) => this.hasFeature(flag));

    if (!meetsFeatures) {
      return false;
    }

    // @ts-expect-error: Incorrect type
    return config.validate(resource);
  }

  hasBookingCurveOnDate(date: Dayjs | string): boolean {
    // It has this tab if one of the message can be shown, or if this data has data to show
    return (
      ((this.hasShortTermRentalAccess && !this.hasShortTermRentalOnlyStrTab) ||
        this.hasFeature('otb.empty_placeholder') ||
        this.hasFeature('otb.data_validation') ||
        (this.hasFeature('pms.otb.view') &&
          this.hotelAndMarketService.hasPaceOnDate(date))) &&
      this.router.currentRouteName?.indexOf('revenue-insight') === -1
    ); // Return false when on Revenue Insight.
  }

  hasFeature(featureFlag: string): boolean {
    return this.featureFlags.has(featureFlag);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  refetchFeatureFlags() {
    if (this.hotel) {
      return this.store.query<Hotel>('hotel', { id: this.hotel.id });
    }
    if (this.market) {
      return this.store.query<Market>('market', { id: this.market.id });
    }

    return null;
  }
}

declare module '@ember/service' {
  interface Registry {
    access: AccessService;
  }
}
